import { Helmet } from "react-helmet-async";
import { generateCombinedMobileSchema } from "@/utils/mobileStructuredData";

interface ProductData {
  id?: string;
  name?: string;
  description?: string;
  image?: string;
  price?: number;
  availability?: boolean;
  category?: string;
  brand?: string;
  itemCount?: number;
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  structuredData?: object;
  pageType?: 'home' | 'product' | 'collection' | 'about' | 'article';
  productData?: ProductData;
  mobileOptimized?: boolean;
}

const SEO = ({ 
  title = "Boho Style Co. - Bohemian Fashion & Jewelry",
  description = "Boho Style Co. – Bohemian fashion, jewelry, and lifestyle accessories.",
  image = "/og-image.jpg",
  url,
  structuredData,
  pageType = 'home',
  productData,
  mobileOptimized = true
}: SEOProps) => {
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : 'https://bohostyleco.com');
  
  // Ensure image URL is absolute for Facebook/social sharing
  const absoluteImage = image.startsWith('http') 
    ? image 
    : `https://bohostyleco.com${image.startsWith('/') ? image : `/${image}`}`;
  
  // Generate mobile-optimized structured data
  const mobileSchemas = mobileOptimized ? 
    generateCombinedMobileSchema(pageType, productData) : 
    [];
    
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      
      {/* Mobile-specific viewport optimizations */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=yes" />
      
      {/* Mobile Safari status bar */}
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      
      {/* Enhanced Open Graph for mobile sharing */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:secure_url" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={pageType === 'article' ? 'article' : 'website'} />
      <meta property="og:site_name" content="Boho Style Co." />
      <meta property="og:locale" content="en_US" />
      
      {/* Enhanced Twitter Cards for mobile */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@bohostyleco" />
      <meta name="twitter:creator" content="@bohostyleco" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Mobile app integration */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="application-name" content="Boho Style Co." />
      
      {/* Performance hints for mobile */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      
      {/* Mobile search optimization */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="msapplication-tap-highlight" content="no" />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="author" content="Boho Style Co." />
      <meta name="language" content="English" />
      <link rel="canonical" href={currentUrl} />
      
      {/* Custom structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Mobile-optimized structured data */}
      {mobileOptimized && mobileSchemas.map((schema, index) => (
        <script key={`mobile-schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;